import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidV4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Square } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import type { PdfField } from "../types";
import { EditorMode } from "../types";
import { type DestinationPacketDocument } from "@/lib/api/destinationPackets";
import { safeParseDecimal } from "@/lib/api/common";
import { fetchApiGet } from "@/lib/api/utils";
import { FieldsList } from "./FieldsList";
import { CreateTemplateDialog } from "./CreateTemplateDialog";
import { CloneFieldsModal } from "./CloneFieldsModal";
import { RoutePath } from "@/section/router";
import {
  PdfDocumentViewer,
  PdfFieldCanvas,
  PdfFieldOverlay,
  PdfSamplePreview,
  usePdfFieldManager,
  type PdfOverlayRender,
  type PdfSource,
} from "@/section/pdf";
import type { PdfFieldMetaShape } from "./fieldTransforms";
import {
  destinationPacketFieldToPdfFieldRect,
  pdfFieldRectToDestinationPacketField,
  pdfFieldRectsToDestinationPacketFields,
} from "./fieldTransforms";

const PDF_WIDTH = 610;

function mergeDestinationPacketField(base: PdfField, updates: Partial<PdfField>): PdfField {
  return {
    ...base,
    ...updates,
    position_data: {
      ...base.position_data,
      ...updates.position_data,
    },
  };
}

export function PdfFieldEditor() {
  const navigate = useNavigate();
  const [pdfSource, setPdfSource] = useState<PdfSource | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [mode, setMode] = useState<EditorMode>("edit");
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [cloneModalOpen, setCloneModalOpen] = useState<boolean>(false);
  const { toast } = useToast();

  const fieldManager = usePdfFieldManager({
    defaultMetaFactory: ({ index }: { index: number }) =>
      ({
        label: `Signature Field ${index + 1}`,
        value: "",
        destinationPacket: {
          fieldKey: `Signature Field ${index + 1}`,
          fieldType: "signature",
          signerId: "1",
          fieldDestType: "text",
          isRequired: true,
          isReadonly: false,
        },
      }) satisfies PdfFieldMetaShape,
  });

  const {
    fields,
    selectedId,
    selectField,
    addField,
    updateField,
    removeField,
    sampleValues,
    sampleList,
    setSampleValue,
    setSampleValues,
    setFields,
    reset,
  } = fieldManager;

  const sampleAwareFields = useMemo(() => {
    return fields.map((field) => {
      const sampleValue = sampleValues[field.id];
      if (typeof sampleValue === "string") {
        return {
          ...field,
          meta: {
            ...(field.meta ?? {}),
            value: sampleValue,
          },
        };
      }
      return field;
    });
  }, [fields, sampleValues]);

  const destinationPacketFields = useMemo(() => pdfFieldRectsToDestinationPacketFields(sampleAwareFields), [sampleAwareFields]);

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || file.type !== "application/pdf") {
        return;
      }

      setPdfSource(file);
      setOriginalFile(file);
      setCurrentPage(1);
      reset([]);
    },
    [reset],
  );

  const handleDocumentLoadSuccess = useCallback(
    ({ totalPages }: { totalPages: number }) => {
      if (currentPage > totalPages) {
        setCurrentPage(totalPages);
      }
    },
    [currentPage],
  );

  const handleUpdateField = useCallback(
    (fieldId: string, updates: Partial<PdfField>) => {
      // Normal update when ID isn't changing
      updateField(fieldId, (rect) => {
        const currentSample = sampleValues[rect.id];
        const rectWithSample =
          typeof currentSample === "string"
            ? {
                ...rect,
                meta: {
                  ...(rect.meta ?? {}),
                  value: currentSample,
                },
              }
            : rect;
        // Convert rect to field, merge updates, then convert back
        // This ensures all updates (including field_key) are properly applied
        const current = pdfFieldRectToDestinationPacketField(rectWithSample);
        const next = mergeDestinationPacketField(current, updates);
        const nextRect = destinationPacketFieldToPdfFieldRect(next);

        // Update sample value if provided
        if (updates.value !== undefined) {
          setSampleValue(nextRect.id, updates.value ?? "");
        }

        return nextRect;
      });
    },
    [sampleValues, setSampleValue, setSampleValues, updateField, fields, setFields, selectField, selectedId],
  );

  const handleDeleteField = useCallback(
    (fieldId: string) => {
      // fieldId should match the rect.id since destinationPacketFieldToPdfFieldRect uses field.field_id as id
      // Verify the field exists before deleting
      const rect = fields.find(f => f.id === fieldId);
      if (rect) {
        removeField(fieldId);
      }
    },
    [removeField, fields],
  );

  const handleDuplicateField = useCallback(
    (fieldId: string) => {
      if (mode !== 'edit') return;

      // Find the field to duplicate
      const fieldToDuplicate = destinationPacketFields.find(f => f.field_id === fieldId);
      if (!fieldToDuplicate) return;

      // Only allow duplicating signature fields
      if (fieldToDuplicate.field_type !== 'signature') return;

      // Find the corresponding rect
      const rectToDuplicate = fields.find(f => f.id === fieldId);
      if (!rectToDuplicate) return;

      // Generate a new unique field ID
      const baseFieldKey = fieldToDuplicate.field_key || fieldToDuplicate.field_id;
      const sanitizedKey = baseFieldKey.toLowerCase().replace(/[^a-z0-9]/g, '_');
      const newFieldId = `signature_${sanitizedKey}_${uuidV4().slice(0, 8)}`;

      // Generate a new unique field name (increment number if it ends with one)
      const nameMatch = baseFieldKey.match(/^(.+?)(\s+(\d+))?$/);
      let newFieldKey: string;
      if (nameMatch && nameMatch[3]) {
        // Field name ends with a number, increment it
        const baseName = nameMatch[1];
        const currentNum = parseInt(nameMatch[3], 10);
        newFieldKey = `${baseName} ${currentNum + 1}`;
      } else {
        // Field name doesn't end with a number, add " 2"
        newFieldKey = `${baseFieldKey} 2`;
      }

      // Calculate new y position (move down by height)
      const newY = fieldToDuplicate.position_data.y - fieldToDuplicate.position_data.height;

      // Create duplicate field with adjusted position
      const duplicatedField: PdfField = {
        ...fieldToDuplicate,
        field_id: newFieldId,
        field_key: newFieldKey,
        position_data: {
          ...fieldToDuplicate.position_data,
          y: newY,
        },
      };

      // Convert to rect format
      const duplicatedRect = destinationPacketFieldToPdfFieldRect(duplicatedField);

      // Find the index of the original field
      const originalIndex = fields.findIndex(f => f.id === fieldId);
      const insertIndex = originalIndex >= 0 ? originalIndex + 1 : fields.length;

      // Insert the duplicated field after the original
      const newFields = [...fields];
      newFields.splice(insertIndex, 0, duplicatedRect);
      setFields(newFields);

      // Set sample value for the new field
      const currentSample = sampleValues[fieldId] || '';
      setSampleValue(duplicatedRect.id, currentSample);

      // Select the new field
      selectField(duplicatedRect.id);
    },
    [mode, destinationPacketFields, fields, sampleValues, setFields, setSampleValue, selectField],
  );

  const handleCreateCheckbox = useCallback(() => {
    if (mode !== 'edit' || !pdfSource) return;

    // Count existing checkbox fields to generate unique name
    const checkboxCount = destinationPacketFields.filter(
      f => f.field_type === 'signature' && f.field_dest_type === 'checkbox'
    ).length;

    // Generate a new unique field ID
    const newFieldId = `signature_checkbox_${uuidV4().slice(0, 8)}`;
    const newFieldKey = `${checkboxCount + 1}`;

    // Default square size (2% of page width/height)
    const checkboxSize = 0.02;
    
    // Position at top of page (y = 1 means top, subtract size to position top-left corner)
    const positionX = 0.1; // 10% from left
    const positionY = 1 - checkboxSize; // Top of page

    // Create checkbox field
    const checkboxField: PdfField = {
      field_id: newFieldId,
      field_key: newFieldKey,
      field_type: 'signature',
      field_dest_type: 'checkbox',
      signer_id: '1',
      position_data: {
        x: positionX,
        y: positionY,
        width: checkboxSize,
        height: checkboxSize,
        page: currentPage,
        text_size: 6
      },
      is_required: false,
      is_readonly: false,
    };

    // Convert to rect format
    const checkboxRect = destinationPacketFieldToPdfFieldRect(checkboxField);

    // Add the field
    const newFields = [...fields, checkboxRect];
    setFields(newFields);

    // Set empty sample value
    setSampleValue(checkboxRect.id, '');

    // Select the new field
    selectField(checkboxRect.id);
  }, [mode, pdfSource, destinationPacketFields, currentPage, fields, setFields, setSampleValue, selectField]);

  const handleOverlaySelect = useCallback(
    (fieldId: string | null) => {
      selectField(fieldId);
    },
    [selectField],
  );

  const handleOverlayFieldUpdate = useCallback(
    (fieldId: string, updates: { x: number; y: number }) => {
      const rect = fields.find((f) => f.id === fieldId);
      if (!rect) return;

      const currentSample = sampleValues[rect.id];
      const rectWithSample =
        typeof currentSample === "string"
          ? {
              ...rect,
              meta: {
                ...(rect.meta ?? {}),
                value: currentSample,
              },
            }
          : rect;

      const current = pdfFieldRectToDestinationPacketField(rectWithSample);
      const next = mergeDestinationPacketField(current, {
        position_data: {
          ...current.position_data,
          x: updates.x,
          y: updates.y,
        },
      });
      const nextRect = destinationPacketFieldToPdfFieldRect(next);

      updateField(fieldId, () => nextRect);
    },
    [fields, sampleValues, updateField],
  );

  const overlayRenderer = useCallback<PdfOverlayRender>(
    (metrics) => {
      const pageFields = sampleAwareFields.filter((field) => field.page === metrics.page);
      const rawPageFields = fields.filter((field) => field.page === metrics.page);

      return (
        <>
          <PdfFieldOverlay
            fields={pageFields}
            metrics={metrics}
            selectedId={selectedId}
            onSelect={(field) => handleOverlaySelect(field.id)}
            onUpdateField={mode === "edit" ? handleOverlayFieldUpdate : undefined}
            draggingEnabled={mode === "edit"}
          />
          {mode === "edit" ? (
            <PdfFieldCanvas
              metrics={metrics}
              fields={rawPageFields}
              onCreateRect={(rect) => addField(metrics.page, rect)}
              onSelectField={(field) => handleOverlaySelect(field?.id ?? null)}
            />
          ) : null}
        </>
      );
    },
    [addField, fields, handleOverlaySelect, handleOverlayFieldUpdate, mode, sampleAwareFields, selectedId],
  );

  const handleOnSuccess = useCallback(() => {
    navigate(RoutePath.DestinationPacketsTemplates, { replace: true });
  }, [navigate]);

  const handleCloneFields = useCallback(async (templateId: string) => {
    try {
      // Fetch the template data
      const result = await fetchApiGet<DestinationPacketDocument>(
        `contracts/destination_packets/template/${templateId}`
      );
      
      if (!result.success || !result.data) {
        throw new Error("Failed to fetch template");
      }
      
      const templateData = result.data;
      const templateFields = templateData?.DocumentTemplate?.Fields ?? [];

      if (templateFields.length === 0) {
        toast({
          title: "No fields found",
          description: "The selected template does not have any fields to clone.",
          variant: "destructive",
        });
        return;
      }

      // Normalize template fields to PdfField format (similar to DestinationPacketsTemplateViewPage)
      const parseDecimal = safeParseDecimal({});
      const toNumber = (value: any) => {
        try {
          return parseDecimal(value ?? 0).toNumber();
        } catch {
          const numeric = Number(value);
          return Number.isFinite(numeric) ? numeric : 0;
        }
      };

      const normalizedFields: PdfField[] = templateFields.map((rawField: any) => {
        const field = rawField as any;
        const position = field.position_data ?? {};

        return {
          field_id: field.field_id,
          field_key: field.field_key,
          field_type: field.field_type === "signature" ? "signature" : "system",
          system_field_type: field.system_field_type,
          field_dest_type: field.field_dest_type,
          signer_id: field.signer_id,
          position_data: {
            x: toNumber(position.x),
            y: toNumber(position.y),
            width: toNumber(position.width),
            height: toNumber(position.height),
            text_size: Number(position.text_size ?? 12),
            page: Number(position.page ?? 1),
          },
          is_required: field.is_required,
          is_readonly: field.is_readonly,
          freeform_value: field.freeform_value,
          value: typeof field.freeform_value === "string" ? field.freeform_value : field.value ?? "",
        } satisfies PdfField;
      });

      // Convert PdfField[] to PdfFieldRect[] and set them
      const fieldRects = normalizedFields.map(destinationPacketFieldToPdfFieldRect);
      setFields(fieldRects);

      // Initialize sample values
      const newSampleValues: Record<string, string> = {};
      normalizedFields.forEach((field) => {
        const value = field.value ?? field.freeform_value ?? "";
        if (value) {
          const rect = fieldRects.find((r) => r.id === field.field_id);
          if (rect) {
            newSampleValues[rect.id] = value;
          }
        }
      });
      setSampleValues(newSampleValues);

      toast({
        title: "Fields cloned",
        description: `Successfully cloned ${normalizedFields.length} field(s) from template.`,
      });
    } catch (error: any) {
      toast({
        title: "Error cloning fields",
        description: error?.message || "Failed to clone fields from template.",
        variant: "destructive",
      });
    }
  }, [setFields, setSampleValues, toast]);

  const disabledPreview = fields.length === 0;

  return (
    <div className="flex h-full flex-col">
      <div className="p-4 border-b bg-white flex flex-row gap-4 justify-between ">
        <div className="flex flex-row items-center">
          <h2 className="text-2xl font-bold">Create Template</h2>
          <Button
            className="ml-4"
            variant={"outline"}
            disabled={fields.length > 0}
            onClick={() => setCloneModalOpen(true)}
          >
            Clone Fields
          </Button>
          <Button
            className="ml-2"
            variant={"default"}
            disabled={fields.length === 0 || !originalFile}
            onClick={() => setDialogOpen(true)}
          >
            Submit Template
          </Button>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="">
            <Input type="file" accept=".pdf" onChange={handleFileUpload} className="max-w-md" />
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant={"outline"}
              className={mode === "edit" ? "bg-yellow-200 hover:bg-yellow-300 text-gray-800 border-yellow-300" : ""}
              onClick={() => setMode("edit")}
            >
              Edit Mode
            </Button>
            <Button
              variant={"outline"}
              className={mode === "preview" ? "bg-yellow-200 hover:bg-yellow-300 text-gray-800 border-yellow-300" : ""}
              onClick={() => setMode("preview")}
              disabled={disabledPreview}
            >
              Preview Mode
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden rounded-lg border bg-background shadow-sm max-h-[100%]">
        <div className="w-full border-r bg-muted/20 lg:w-1/3 overflow-y-scroll">
          <FieldsList
            fields={destinationPacketFields}
            selectedField={selectedId}
            onSelectField={handleOverlaySelect}
            onUpdateField={handleUpdateField}
            onDeleteField={handleDeleteField}
            onDuplicateField={mode === 'edit' ? handleDuplicateField : undefined}
            mode={mode}
          />
        </div>

        <div className="flex-1 bg-muted/30 overflow-hidden">
          {pdfSource ? (
            mode === "edit" ? (
              <PdfDocumentViewer
                source={pdfSource}
                width={PDF_WIDTH}
                page={currentPage}
                onPageChange={setCurrentPage}
                onDocumentLoad={handleDocumentLoadSuccess}
                renderOverlay={overlayRenderer}
                toolbarActions={
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCreateCheckbox}
                    title="Create checkbox"
                  >
                    <Square className="h-4 w-4 mr-1" />
                    Checkbox
                  </Button>
                }
              />
            ) : (
              <PdfSamplePreview
                source={pdfSource}
                fields={fields}
                samples={sampleList}
                width={PDF_WIDTH}
                page={currentPage}
                onPageChange={setCurrentPage}
                onDocumentLoad={handleDocumentLoadSuccess}
              />
            )
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <p className="text-lg mb-2">No PDF loaded</p>
                <p className="text-sm">Upload a PDF file to begin editing</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {dialogOpen && originalFile ? (
        <CreateTemplateDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          fields={destinationPacketFields}
          originalFile={originalFile}
          onSuccess={handleOnSuccess}
        />
      ) : null}

      <CloneFieldsModal
        open={cloneModalOpen}
        onOpenChange={setCloneModalOpen}
        onSelectTemplate={handleCloneFields}
      />
    </div>
  );
}

